import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';

interface PrivacyPageProps {
  onBack: () => void;
}

const LAST_UPDATED = 'July 2, 2026';
const SITE_NAME = 'Get Out Pitt';
const SITE_URL = 'https://getoutpitt.com';
const CONTACT_EMAIL = 'privacy@getoutpitt.com';

export default function PrivacyPage({ onBack }: PrivacyPageProps) {
  useEffect(() => {
    const prevTitle = document.title;
    const descEl = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = descEl?.content ?? '';
    document.title = 'Privacy & Cookie Policy — Get Out Pitt';
    descEl?.setAttribute('content', 'Read the Get Out Pitt privacy and cookie policy. Learn how we collect, use, and protect your data, and how to manage cookie preferences.');
    return () => {
      document.title = prevTitle;
      descEl?.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-zinc-950 text-white z-20 border-b border-zinc-800/80">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <span className="text-[17px] font-bold tracking-tight leading-none">
            Get Out <span className="text-amber-400">Pitt</span>
          </span>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy &amp; Cookie Policy</h1>
        <p className="text-sm text-slate-400 mb-10">Last updated: {LAST_UPDATED}</p>

        <Prose>
          <p>
            This Privacy &amp; Cookie Policy explains how {SITE_NAME} ("{SITE_NAME}", "we", "us",
            or "our") collects, uses, and shares information when you visit {SITE_URL} (the
            "Site"). By using the Site you agree to the practices described below.
          </p>
        </Prose>

        <Section title="1. Information We Collect">
          <Prose>
            <h3>Information you provide directly</h3>
            <ul>
              <li>
                <strong>Contact form submissions</strong> — when you use our Contact page we
                collect your name, email address, and the content of your message. We use this
                only to respond to your inquiry.
              </li>
            </ul>

            <h3>Information collected automatically</h3>
            <ul>
              <li>
                <strong>Usage data</strong> — standard server/CDN logs may record your IP
                address, browser type, referring URL, and pages visited. This data is used
                for security monitoring and aggregate analytics only.
              </li>
              <li>
                <strong>Location data</strong> — if you click "My Location", your browser
                requests your device's GPS coordinates. We use this only to center the map;
                we do not store or transmit your precise location to our servers.
              </li>
            </ul>

          </Prose>
        </Section>

        <Section title="2. How We Use Your Information">
          <Prose>
            <ul>
              <li>To respond to contact form messages</li>
              <li>To display an interactive map of local events</li>
              <li>To monitor site security and performance</li>
            </ul>
            <p>
              We do not sell, rent, or share your personal information with third parties
              except as described in this policy or required by law.
            </p>
          </Prose>
        </Section>

        <Section title="3. Cookies &amp; Tracking Technologies">
          <Prose>
            <p>
              Cookies are small text files stored on your device. We use the following
              categories:
            </p>

            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Purpose</th>
                  <th>Required</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Essential</td>
                  <td>
                    Stores your cookie consent preference so we don't ask on every visit
                  </td>
                  <td>Yes</td>
                </tr>
              </tbody>
            </table>

            <h3>Managing cookies</h3>
            <p>
              You can withdraw consent at any time by clicking{' '}
              <button
                onClick={() => {
                  localStorage.removeItem('cookie_consent');
                  window.location.reload();
                }}
                className="text-amber-600 underline underline-offset-2 hover:text-amber-700"
              >
                Reset my cookie preferences
              </button>
              . You can also block or delete cookies in your browser settings; note that
              essential cookies are necessary for the site to function.
            </p>
          </Prose>
        </Section>

        <Section title="4. Your Rights (GDPR / CCPA)">
          <Prose>
            <p>
              Depending on where you are located, you may have rights regarding your
              personal data, including:
            </p>
            <ul>
              <li>
                <strong>Access</strong> — request a copy of personal data we hold about you
              </li>
              <li>
                <strong>Correction</strong> — request correction of inaccurate data
              </li>
              <li>
                <strong>Deletion</strong> — request that we delete your data
              </li>
              <li>
                <strong>Objection / restriction</strong> — object to or restrict how we
                process your data
              </li>
              <li>
                <strong>Withdrawal of consent</strong> — withdraw cookie consent at any
                time (see Section 3)
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will respond
              within 30 days.
            </p>
            <p>
              <strong>California residents (CCPA)</strong>: We do not sell personal
              information. You have the right to know what data we collect and to request
              its deletion.
            </p>
          </Prose>
        </Section>

        <Section title="5. Data Retention">
          <Prose>
            <p>
              Contact form messages are retained until manually deleted by our team.
              Server logs are automatically purged after 90 days. We do not retain
              location data beyond your browser session.
            </p>
          </Prose>
        </Section>

        <Section title="6. Children's Privacy">
          <Prose>
            <p>
              The Site is not directed to children under 13. We do not knowingly collect
              personal information from children. If you believe a child has submitted
              personal information to us, please contact us immediately.
            </p>
          </Prose>
        </Section>

        <Section title="7. Third-Party Links">
          <Prose>
            <p>
              The Site links to third-party event pages. Once you leave our Site, this
              policy no longer applies and we are not responsible for the privacy practices
              of those sites.
            </p>
          </Prose>
        </Section>

        <Section title="8. Changes to This Policy">
          <Prose>
            <p>
              We may update this policy from time to time. The "Last updated" date at the
              top will reflect any changes. Continued use of the Site after an update
              constitutes acceptance of the revised policy.
            </p>
          </Prose>
        </Section>

        <Section title="9. Contact Us">
          <Prose>
            <p>
              Questions about this policy? Contact us:
            </p>
            <ul>
              <li>
                Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </li>
              <li>
                Web form:{' '}
                <button
                  onClick={onBack}
                  className="text-amber-600 underline underline-offset-2 hover:text-amber-700"
                >
                  Contact page
                </button>
              </li>
            </ul>
          </Prose>
        </Section>
      </main>

      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">
        <p>&copy; {new Date().getFullYear()} {SITE_NAME}. All rights reserved.</p>
      </footer>
    </div>
  );
}

// ── Layout helpers ────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-200">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div className="
      prose prose-sm prose-slate max-w-none
      prose-headings:font-semibold prose-headings:text-slate-800 prose-headings:mt-5 prose-headings:mb-2
      prose-p:text-slate-600 prose-p:leading-relaxed
      prose-li:text-slate-600 prose-li:leading-relaxed
      prose-a:text-amber-600 prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-amber-700
      prose-strong:text-slate-700
      prose-table:text-sm
      prose-th:text-left prose-th:font-semibold prose-th:text-slate-700 prose-th:py-2 prose-th:pr-6
      prose-td:py-2 prose-td:pr-6 prose-td:text-slate-600 prose-td:align-top
    ">
      {children}
    </div>
  );
}
